import { makeObservable, action, computed, observable, reaction, autorun } from 'mobx';
import { message } from 'antd';
import { ContractStatus, ContractSubmissionRequestStateDto } from '../types';
import { Disposer } from '../../custom_shared/misc';
import { ContractsIngestionService } from '../services';
import { FormStore } from '../../custom_shared/stores';
import ActionStore from './ActionStore';
import NodesStore from './NodesStore';
import SessionStore from './SessionStore';

export default class ContractSubmissionStore extends Disposer {
    contractStatus: ContractStatus = null;
    submissionRequestState: ContractSubmissionRequestStateDto = {
        status: null
    };

    private submissionRequestInterval: ReturnType<typeof setInterval> | null = null;

    constructor(
        private readonly formStore: FormStore,
        private readonly nodesStore: NodesStore,
        private readonly sessionStore: SessionStore,
        private readonly actionStore: ActionStore,
        private readonly service: ContractsIngestionService
    ) {
        super();

        makeObservable(this, {
            contractStatus: observable,
            submissionRequestState: observable,
            setContractStatus: action,
            setSubmissionRequestState: action,
            contractSending: computed,
            contractSent: computed,
            contractSendingOrSent: computed,
            requestProcessingDelayed: computed,
            contractProcessingResults: computed,
            contractTitle: computed,
            programId: computed,
            businesses: computed
        });

        this.reactions.push(
            reaction(
                () => this.sessionStore.response,
                () => {
                    if (this.sessionStore.response && !this.sessionStore.inProgress) {
                        this.setContractStatus(this.sessionStore.response.contractStatus);
                    }
                }
            )
        );

        // TODO: create iota plugin webosocket connection to get updates
        this.reactions.push(
            autorun(() => {
                if (this.submissionRequestInterval) {
                    clearInterval(this.submissionRequestInterval);
                }

                // Once we get the delayed status, we don't need to keep checking
                if (this.requestProcessingDelayed) {
                    return;
                }

                if (this.contractSending) {
                    this.submissionRequestInterval = setInterval(() => {
                        this.getContractSubmissionRequestState();
                    }, 1000);
                }
            })
        );

        this.getContractSubmissionRequestState();
    }

    setContractStatus(contractStatus: ContractStatus) {
        this.contractStatus = contractStatus;
    }

    setSubmissionRequestState(submissionRequestState: ContractSubmissionRequestStateDto) {
        this.submissionRequestState = submissionRequestState;
    }

    async getContractSubmissionRequestState() {
        const response = await this.service.getContractSubmissionRequestState(
            this.sessionStore.sessionExtension,
            this.sessionStore.sessionId
        );

        if (response.isOk()) {
            response.map(state => this.setSubmissionRequestState(state));
        }
    }

    get contractSending() {
        return this.contractStatus == 'ContractSending';
    }

    get contractSent() {
        return this.contractStatus == 'ContractSent';
    }

    get contractSendingOrSent() {
        return this.contractSending || this.contractSent;
    }

    get requestProcessingDelayed() {
        return this.submissionRequestState.status === 'RequestProcessingDelayed';
    }

    get contractProcessingResults() {
        return this.sessionStore.response?.contractProcessingResults ?? null;
    }

    get contractTitle() {
        return this.contractProcessingResults?.contractTitle ?? '';
    }

    get programId() {
        return this.contractProcessingResults?.program?.programId ?? '';
    }

    get businesses() {
        return this.contractProcessingResults?.businesses ?? [];
    }

    async sendContract(): Promise<void> {
        if (this.contractSendingOrSent) {
            return;
        }

        const validations = await this.formStore.validateForms();

        let allValid = true;

        validations.forEach(validation => {
            if (!validation.success) {
                allValid = false;
            }

            const node = this.nodesStore.nodes.find(t => t.id === validation.nodeId);

            if (node) {
                node.setHasError(!validation.success);
            }
        });

        if (allValid) {
            this.setContractStatus('ContractSending');
            return this.actionStore.sendContract();
        }

        message.error('Please complete all required fields to proceed');
    }

    dispose() {
        this.disposeReactions();
    }
}
